<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">

          <v-card class="pa-4">
            <div class="d-flex justify-space-between mb-4">
              <h2 class="text-h6 font-weight-bold">Budget Type List</h2>

              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi mdi-plus"
                rounded="xl"
                @click="openCreate"
              >
                New Budget Type
              </v-btn>
            </div>

            <v-data-table
              :headers="headers"
              :items="budgetTypeList"
              class="elevation-1"
            >

              <template #item.index="{ index }">
                {{ ++index }}
              </template>

              <template #item.nature="{ item }">
                <v-chip
                  :color="item.nature === 'Income' ? 'green' : 'orange'"
                  size="small"
                >
                  {{ item.nature }}
                </v-chip>
              </template>

              <template #item.status="{ item }">
                <v-chip
                  v-if="item.status == 1"
                  color="success"
                  size="small"
                >
                  Active
                </v-chip>
                <v-chip v-else color="red" size="small">
                  Inactive
                </v-chip>
              </template>

              <template #item.actions="{ item }">
                <v-icon
                  class="me-2"
                  color="primary"
                  @click="editBudgetType(item)"
                >
                  mdi-pencil-outline
                </v-icon>
              </template>

            </v-data-table>
          </v-card>

        </v-col>
      </v-row>

      <!-- MODAL -->
      <v-dialog v-model="dialog" max-width="500">
        <v-card class="pa-3">

          <v-card-title>
            {{ isEdit ? "Update Budget Type" : "Create Budget Type" }}
          </v-card-title>

          <v-card-text>
            <v-row>

              <v-col cols="12">
                <v-text-field
                  v-model="name"
                  label="Name *"
                  variant="outlined"
                  density="compact"
                  :error-messages="nameError"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-select
                  v-model="nature"
                  :items="['Income','Expense']"
                  label="Nature *"
                  variant="outlined"
                  density="compact"
                  :error-messages="natureError"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  label="Active"
                  v-model="status"
                  :true-value="1"
                  :false-value="0"
                />
              </v-col>

            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-actions>
                <v-spacer />
                <v-btn class="bg-red" @click="dialog=false"> Close </v-btn>
                <v-btn class="bg-brand w-15 ms-2" @click="saveBudgetType">  {{ isEdit ? "Update" : "Create" }}  </v-btn>
          </v-card-actions>

        </v-card>
      </v-dialog>

    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import { useStore } from "vuex"

import { useForm, useField } from "vee-validate"
import * as yup from "yup"

const store = useStore()

/* -----------------------------
STATE
--------------------------------*/
const dialog = ref(false)
const isEdit = ref(false)
const budgetTypeId = ref(null)

/* -----------------------------
VALIDATION
--------------------------------*/
const schema = yup.object({
  name: yup.string().required("Name is required"),
  nature: yup.string().required("Nature is required"),
  status: yup.number()
})

const { handleSubmit, setErrors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    name: "",
    nature: "",
    status: 1
  }
})

const { value: name, errorMessage: nameError } = useField("name")
const { value: nature, errorMessage: natureError } = useField("nature")
const { value: status } = useField("status")

/* -----------------------------
TABLE
--------------------------------*/
const headers = [
  { title: "#SL", key: "index" },
  { title: "Code", key: "code" },
  { title: "Name", key: "name" },
  { title: "Nature", key: "nature" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions" }
]

const budgetTypeList = computed(() => {
  return store.getters.getBudgetTypeList
})

/* -----------------------------
LOAD DATA
--------------------------------*/
const loadBudgetTypes = async () => {
  store.commit("SET_LOADING", { color: "primary" })
  await store.dispatch("getBudgetTypeList")
  store.commit("UNSET_LOADING")
}

/* -----------------------------
CREATE
--------------------------------*/
const openCreate = () => {
  isEdit.value = false

  resetForm({
    values: {
      name: "",
      nature: "",
      status: 1
    }
  })

  dialog.value = true
}

/* -----------------------------
EDIT
--------------------------------*/
const editBudgetType = async (item) => {
  isEdit.value = true
  budgetTypeId.value = item.id

  const response = await store.dispatch("getBudgetTypeById", item.id)

  const data = response.data

  name.value = data.name
  nature.value = data.nature
  status.value = data.status

  dialog.value = true
}

/* -----------------------------
PAYLOAD
--------------------------------*/
const buildPayload = () => {
  return {
    name: name.value,
    nature: nature.value,
    status: status.value
  }
}

/* -----------------------------
SAVE
--------------------------------*/
const saveBudgetType = handleSubmit(async () => {
  try {
    const payload = buildPayload()

    if (isEdit.value) {
      payload.id = budgetTypeId.value
      await store.dispatch("updateBudgetType", payload)
    } else {
      await store.dispatch("saveBudgetType", payload)
    }

    dialog.value = false
    loadBudgetTypes()

  } catch (error) {
    if (error.response && error.response.status === 422) {
      setErrors(error.response.data.errors)
    }
  }
})

/* -----------------------------
MOUNT
--------------------------------*/
onMounted(() => {
  loadBudgetTypes()
})
</script>